import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, AlertTriangle, CheckCircle2, Sparkles, BedDouble, Wallet, Wrench, Globe2 } from "lucide-react";

/**
 * Attention inbox — compact list of actionable items.
 * NO large colored alert background.
 * Only a small severity dot and subtle hover surface.
 */
export default function AttentionInbox({ overview, dirtyArrivalCount, unassignedArrivals, badChannels }) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const items = useMemo(() => {
        if (!overview) return [];
        const list = [];
        if (dirtyArrivalCount > 0) list.push({
            severity: "critical", icon: Sparkles, key: "dirty",
            text: t("dashboard.att_dirty_arrivals", { count: dirtyArrivalCount }),
            to: "/housekeeping",
        });
        if (unassignedArrivals > 0) list.push({
            severity: "warning", icon: BedDouble, key: "unassigned",
            text: t("dashboard.att_unassigned", { count: unassignedArrivals }),
            to: "/reservations",
        });
        if ((overview.pending_payments_count || 0) > 0) list.push({
            severity: "warning", icon: Wallet, key: "pending",
            text: t("dashboard.att_pending_payments", { count: overview.pending_payments_count }),
            to: "/billing",
        });
        if ((overview.out_of_order || 0) > 0) list.push({
            severity: "critical", icon: Wrench, key: "oos",
            text: t("dashboard.att_out_of_service", { count: overview.out_of_order }),
            to: "/rooms",
        });
        if ((overview.maintenance || 0) > 0) list.push({
            severity: "info", icon: Wrench, key: "maint",
            text: t("dashboard.att_maintenance", { count: overview.maintenance }),
            to: "/rooms",
        });
        if (badChannels > 0) list.push({
            severity: "warning", icon: Globe2, key: "channels",
            text: t("dashboard.att_channel_issues", { count: badChannels }),
            to: "/channels",
        });
        return list;
    }, [overview, dirtyArrivalCount, unassignedArrivals, badChannels, t]);

    return (
        <section className="rounded-[10px] border border-border bg-card" data-testid="attention-card">
            <div className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-2">
                    <AlertTriangle size={14} className="text-[#ff7b02]" />
                    <div className="text-[14px] font-semibold text-foreground">
                        {t("dashboard.needs_attention")}
                    </div>
                </div>
                {items.length > 0 && (
                    <span className="text-[12px] font-semibold text-muted-foreground tabular-nums">
                        {items.length}
                    </span>
                )}
            </div>

            {items.length === 0 ? (
                <div className="flex items-center gap-2 px-5 pb-5 pt-1 text-[13px] text-emerald-700">
                    <CheckCircle2 size={15} className="text-emerald-500" />
                    {t("dashboard.no_attention")}
                </div>
            ) : (
                <ul className="px-2 pb-2">
                    {items.map((a) => (
                        <li key={a.key}>
                            <button
                                onClick={() => navigate(a.to)}
                                data-testid={`attention-item-${a.key}`}
                                className="group flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition hover:bg-muted/60"
                            >
                                <SeverityDot severity={a.severity} />
                                <span className="flex-1 text-[13px] leading-snug text-foreground">
                                    {a.text}
                                </span>
                                <ChevronRight size={14} className="text-muted-foreground/40 transition group-hover:translate-x-0.5 group-hover:text-muted-foreground" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

function SeverityDot({ severity }) {
    const cls = {
        critical: "bg-rose-500",
        warning: "bg-amber-500",
        info: "bg-sky-500",
    }[severity] || "bg-slate-400";
    return <span className={`inline-flex h-1.5 w-1.5 shrink-0 rounded-full ${cls}`} />;
}
